import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  MenuItem,
  Select,
  InputLabel,
  FormControl
} from '@mui/material';
import MainCard from 'components/MainCard';
import { getReport } from 'services/report/getReport';
import { useQuery } from '@tanstack/react-query';

const months = [
  { name: 'January', days: 31 },
  { name: 'February', days: 28 },
  { name: 'March', days: 31 },
  { name: 'April', days: 30 },
  { name: 'May', days: 31 },
  { name: 'June', days: 30 },
  { name: 'July', days: 31 },
  { name: 'August', days: 31 },
  { name: 'September', days: 30 },
  { name: 'October', days: 31 },
  { name: 'November', days: 30 },
  { name: 'December', days: 31 }
];

export default function SessionAnalytics() {
  // Use current month as default
  const [selectedMonth, setSelectedMonth] = useState(months[new Date().getMonth()].name);

  const daysInMonth = months.find((m) => m.name === selectedMonth).days;
  const dayNumbers = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const coachId = localStorage.getItem('userid');
  const currentRoleId = localStorage.getItem('roleid');

  const { data, isLoading, error } = useQuery({
    enabled: !!coachId, 
    queryKey: ['reports', coachId, currentRoleId],
    queryFn: () => getReport({ 
      coachId: Number(coachId),
      roleid: currentRoleId === '4' ? 5 : undefined
    }),
  });

  const allReports = data?.data || [];
  // Group reports by student for the selected month
  const studentsMap = {};
  allReports.forEach((report) => {
    const dateObj = new Date(report.date);
    const monthName = months[dateObj.getMonth()].name;
    if (monthName !== selectedMonth) return;
    const day = dateObj.getDate();
    if (!studentsMap[report.studentName]) {
      studentsMap[report.studentName] = {
        attendance: Array(daysInMonth).fill('N'),
        performances: Array(daysInMonth).fill(null),
        studentName: report.studentName,
      };
    }
    studentsMap[report.studentName].attendance[day - 1] = 'A';
    studentsMap[report.studentName].performances[day - 1] = report.performance;
  });
  // Calculate avg accuracy for each student
  const students = Object.values(studentsMap).map((student) => {
    // For each day, use 0 if no performance, else the value
    const performancesWithZero = student.performances.map((p) => (p !== null ? p : 0));
    const avgAccuracy = performancesWithZero.length > 0
      ? (performancesWithZero.reduce((a, b) => a + b, 0) / performancesWithZero.length).toFixed(1)
      : 'NA';
    return {
      studentName: student.studentName,
      attendance: student.attendance,
      avgAccuracy,
    };
  });

  return (
    <MainCard>
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', py: 2 }}>
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel id="month-select-label">Month</InputLabel>
          <Select
            labelId="month-select-label"
            id="month-select"
            value={selectedMonth}
            label="Month"
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            {months.map((month) => (
              <MenuItem key={month.name} value={month.name}>
                {month.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 600 }} aria-label="student table">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Student Name</TableCell>
              {dayNumbers.map((day) => (
                <TableCell key={day} align="center">{day}</TableCell>
              ))}
              <TableCell>Score</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {students.length > 0 ? (
              students.map((student, index) => (
                <TableRow key={student.studentName}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{student.studentName}</TableCell>
                  {dayNumbers.map((day) => {
                    // Determine if this day is in the future
                    const now = new Date();
                    const currentYear = now.getFullYear();
                    const currentMonth = now.getMonth(); // 0-indexed
                    const today = now.getDate();
                    const selectedMonthIndex = months.findIndex((m) => m.name === selectedMonth);
                    // Assume current year for simplicity; adjust if year selection is added
                    let isFuture = false;
                    if (selectedMonthIndex > currentMonth) {
                      isFuture = true;
                    } else if (selectedMonthIndex === currentMonth && day > today) {
                      isFuture = true;
                    }
                    if (isFuture) {
                      return (
                        <TableCell
                          key={day}
                          align="center"
                          sx={{
                            backgroundColor: '#e0e0e0', // light gray
                            color: '#888',
                          }}
                        >
                          {/* Empty cell for future days */}
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell
                        key={day}
                        align="center"
                        sx={{
                          backgroundColor:
                            student.attendance[day - 1] === 'A'
                              ? '#c8e6c9' // light green
                              : student.attendance[day - 1] === 'N'
                              ? '#ffcdd2' // light red
                              : 'inherit',
                          color: '#222',
                          fontWeight: 'bold',
                        }}
                      >
                        {student.attendance[day - 1]}
                      </TableCell>
                    );
                  })}
                  <TableCell>{student.avgAccuracy}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={dayNumbers.length + 3} align="center">
                  No data available for this month.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Legend */}
      <Box sx={{ mt: 2, display: 'flex', gap: 3, alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 18, height: 18, bgcolor: '#c8e6c9', border: '1px solid #388e3c', borderRadius: '3px' }} />
          <span style={{ fontWeight: 500 }}>A - Attended</span>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 18, height: 18, bgcolor: '#ffcdd2', border: '1px solid #d32f2f', borderRadius: '3px' }} />
          <span style={{ fontWeight: 500 }}>N - Not attended</span>
        </Box>
      </Box>
    </MainCard>
  );
}
